import { Styles } from 'react-select';

import styled from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

export const SELECT_ZINDEX = 99999;

export const InputBox = styled.input({
  border: 'none',
  backgroundColor: 'transparent',
  font: theme.font.regular16,
  color: theme.form.value.color,
  outline: 'none',
});

export const styles: Styles<any, boolean> = {
  clearIndicator: (provided, state) => ({
    ...provided,
    padding: 0,
    svg: {
      cursor: 'pointer',
      ':hover': {
        color: `${theme.form.icon.colorHover} !important`,
      },
      color: state.isFocused ? theme.form.icon.colorFocus : theme.form.icon.color,
    },
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    padding: `${pxToRem(0)} ${pxToRem(8)}`,
    svg: {
      cursor: 'pointer',
      ':hover': {
        color: `${theme.form.icon.colorHover} !important`,
      },
      color: `${state.isFocused ? theme.form.icon.colorFocus : theme.form.icon.color} !important`,
    },
  }),
  menu: (provided, state) => ({
    ...provided,
    backgroundColor: theme.color.backgroundLightDark,
    borderRadius: '0.25rem',
    margin: 0,
    boxShadow: '0px 0.5rem 1.5rem rgba(0, 0, 0, 0.16)',
    zIndex: SELECT_ZINDEX,
    width: '100%',
    ...(state.selectProps.overflowWidth && {
      width: 'max-content',
    }),
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '1rem',
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: SELECT_ZINDEX,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: theme.form.option.bg.color,
    font: theme.font.regular14,
    color: theme.form.option.color,
    minHeight: theme.base.height,
    display: 'flex',
    padding: 0,
    alignItems: 'center',
    borderRadius: theme.form.border.radius,
    ...(state.isFocused && {
      backgroundColor: theme.form.option.bg.colorFocus,
    }),
    ...(state.isDisabled && {
      opacity: '50%',
    }),
    ':hover': {
      color: theme.form.option.colorHover,
      cursor: 'pointer',
    },
    ':active': {},
    '> div': {
      lineHeight: '1.25rem',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      display: 'inline-block',
      ...(state.selectProps.optionContentFullWidth && {
        display: 'block',
        width: '100%',
      }),
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: `${pxToRem(0)} ${pxToRem(8)}`,
    cursor: 'pointer',
  }),
  singleValue: (provided, state) => ({
    ...provided,
    marginLeft: pxToRem(4),
    color: theme.form.value.color,
    ...(state.selectProps.optionContentFullWidth && {
      width: '100%',
      display: 'block',
    }),
    '.synced-icon': {
      marginLeft: pxToRem(8),
      marginRight: pxToRem(12),
    },
  }),
  control: (provided, state) => ({
    ...provided,
    boxShadow: 'none',
    font: theme.font.regular16,
    transition: transitionStandard('border-color', 'background-color'),
    borderWidth: theme.form.border.width,
    borderStyle: 'solid',
    borderColor: state.selectProps.error
      ? theme.color.elementRed100
      : state.isFocused
      ? theme.form.border.colorFocus
      : theme.form.border.color,
    backgroundColor: state.selectProps.error ? theme.color.redTrans5 : theme.form.bg.color,
    borderRadius: theme.form.border.radius,
    height: theme.base.height,
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    outline: 'none',
    '&:hover': {
      '[class*="-indicatorsContainer"] svg': {
        cursor: 'pointer',
        color: theme.form.icon.color,
      },
    },
    '&:active': {},
    '&:focus': {},
    ...(!state.isFocused && {
      '&:hover': {
        backgroundColor: theme.form.bg.colorHover,
      },
    }),
    ...(state.isFocused && {
      backgroundColor: theme.form.bg.colorFocus,
    }),
  }),
  placeholder: (provided, state) => ({
    ...provided,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: state.isFocused ? theme.form.placeholder.colorFocus : theme.form.placeholder.color,
    marginLeft: pxToRem(4),
  }),
};

export const FocusIconContainer = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  paddingRight: '0.75rem',
  svg: {
    color: theme.form.icon.color,
  },
}));

export const OptionContainer = styled.div<{ $helpText: string }>((p) => ({
  padding: p.$helpText ? '0.75rem' : '0.625rem 0.75rem',
  font: p.$helpText ? theme.font.semibold14 : theme.font.regular14,
  display: 'flex',
  flexDirection: 'column',
}));

export const HelpText = styled.div({
  font: theme.font.regular14,
  color: theme.color.textHelper,
  textWrap: 'wrap',
});

export const PrefixContainer = styled.span({
  display: 'flex',
  alignItems: 'center',
});

export const Prefix = styled.span({
  color: theme.color.textHelper,
  marginRight: '0.25rem',
});

export const MenuListPositionWrapper = styled.div({
  position: 'relative',
  zIndex: 9991,
});
